import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../utils/firebaseConfig';
import { cacheService, CACHE_KEYS } from '../utils/cacheService';
import GeneralNavbar from './GeneralNavbar';
import './EditProduct.css';

function EditProduct() {
  const { id } = useParams(); // Obtener el ID del producto desde la URL
  const navigate = useNavigate(); // Para redirigir después de la actualización
  const [product, setProduct] = useState(null); // Estado para almacenar los datos del producto
  const [loading, setLoading] = useState(true); // Estado para mostrar carga
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stock: '',
    price: '',
    category: '', // Campo de categoría
    brand: '', // Campo de marca
    discount: '', // Campo de descuento (en porcentaje)
    discountInPesos: '', // 👈 nuevo
  });
  const [image, setImage] = useState(null); // Estado para la imagen seleccionada
  const [preview, setPreview] = useState(null); // Estado para mostrar vista previa de la imagen
  const [productId, setProductId] = useState(null); // Inicializa productId con un valor nulo
  const [isSubmitting, setIsSubmitting] = useState(false); // Manejar el estado de envío
  const [categories, setCategories] = useState([]); // Lista de categorías
  const [brands, setBrands] = useState([]); // Lista de marcas según categoría

  // 🔹 Cargar marcas según categoría seleccionada (con caché)
  const loadBrandsByCategory = async (categoryId) => {
    try {
      // 🚀 Verificar caché primero
      const cacheKey = CACHE_KEYS.BRANDS(categoryId);
      const cached = cacheService.get(cacheKey);
      if (cached) {
        setBrands(cached);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const brandsSnapshot = await getDocs(
        collection(db, 'categories', categoryId, 'brands')
      );
      
      const brandList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(cacheKey, brandList);
      
      setBrands(brandList);
    } catch (error) {
      console.error("Error cargando marcas:", error);
      setBrands([]);
    }
  };

  // 🔹 Manejar cambio de marca - actualizar name automáticamente
  const handleBrandChange = (brandId) => {
    setFormData({ ...formData, brand: brandId });
    const selectedBrand = brands.find(b => b.id === brandId);
    if (selectedBrand) {
      setFormData(prevData => ({ ...prevData, name: selectedBrand.name })); // Automáticamente asignar nombre de marca
    }
  };

  // Función para manejar el retroceso
  const handleBackClick = () => {
    navigate('/admin/products'); // Redirige al componente Admin (ajusta la ruta según sea necesario)
  };

  // 🔹 Cargar categorías al montar el componente
  useEffect(() => {
    loadCategories();
  }, []);

  // 🔹 Cargar marcas cuando cambia la categoría
  useEffect(() => {
    if (formData.category) {
      loadBrandsByCategory(formData.category);
    } else {
      setBrands([]);
    }
  }, [formData.category]);

  // 🔹 Cargar lista de categorías (con caché)
  const loadCategories = async () => {
    try {
      // 🚀 Verificar caché primero
      const cached = cacheService.get(CACHE_KEYS.CATEGORIES);
      if (cached) {
        setCategories(cached);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const categoryList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(CACHE_KEYS.CATEGORIES, categoryList);
      
      setCategories(categoryList);
    } catch (error) {
      console.error("Error cargando categorías:", error);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!id) {
          console.error('No se proporcionó un ID de producto');
          return;
        }

        // Consultar la colección para encontrar el documento donde el campo 'id' coincide
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('id', '==', Number(id))); // Busca por el campo 'id' que es numérico
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const productData = doc.data();
            setProduct(productData);

            // Guarda el ID del documento de Firestore para usarlo en la actualización
            setProductId(doc.id);

            setFormData({
              name: productData.name,
              description: productData.description,
              stock: productData.stock,
              price: productData.price,
              category: productData.category || '',
              brand: productData.brand || '',
              discount: productData.discount || '',
              discountInPesos: productData.discountInPesos || '', // 👈 agregar este
            });

            setPreview(productData.image); // Establecer la imagen existente
          });
        } else {
          console.error('Producto no encontrado en Firestore.');
        }
      } catch (error) {
        console.error('Error al obtener el producto: ', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData((prevData) => {
    // Si se modifica 'discount', limpiar 'discountInPesos'
    if (name === 'discount') {
      return { ...prevData, discount: value, discountInPesos: '' };
    }
    // Si se modifica 'discountInPesos', limpiar 'discount'
    if (name === 'discountInPesos') {
      return { ...prevData, discountInPesos: value, discount: '' };
    }

    return { ...prevData, [name]: value };
  });
};


  // Manejar cambio de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file)); // Mostrar la imagen seleccionada
    }
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoading(true);
    try {
      let imageUrl = product?.image || ''; // Usar la URL existente de la imagen si ya hay una

      // Si se ha seleccionado una nueva imagen
      if (image) {
        const storage = getStorage();
        const imageRef = ref(storage, `product-images/${id}-${image.name}`);
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Utiliza el ID del documento Firestore guardado
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        ...formData,
        discount: formData.discount ? parseFloat(formData.discount) : 0,
        discountInPesos: formData.discountInPesos ? parseFloat(formData.discountInPesos) : 0,
        image: imageUrl,
      });


      navigate('/admin/products');
    } catch (error) {
      console.error('Error actualizando el producto: ', error);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="product-form-wrapper">
      <GeneralNavbar title="Editar Producto" onBackClick={handleBackClick}/>
      {loading ? (
        <p className="centered-text">Cargando...</p>
      ) : product ? (
        <div className="product-form-container">
          <form onSubmit={handleSubmit}>
            <div className="product-form-group">
              <label className="product-form-label">Categoría:</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="product-form-input"
                required
              >
                <option value="">Selecciona una categoría</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 🔹 Menú desplegable para Marca (dinámico) - REEMPLAZA al input de nombre */}
            {formData.category && (
              <div className="product-form-group">
                <label className="product-form-label">Marca del Producto:</label>
                <select 
                  className="product-form-input"
                  value={formData.brand}
                  onChange={(e) => handleBrandChange(e.target.value)}
                  required
                >
                  <option value="">Selecciona una marca</option>
                  {brands.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="product-form-group">
              <label className="product-form-label">Descripción:</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="product-form-input"
                required
              />
            </div>
            <div className="product-form-group">
              <label className="product-form-label">Stock:</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                className="product-form-input"
                required
              />
            </div>
            <div className="product-form-group">
              <label className="product-form-label">Precio:</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="product-form-input"
                required
              />
            </div>
            <div className="product-form-group">
            <label className="product-form-label">Descuento en porcentaje (%):</label>
            <input
              type="number"
              name="discount"
              value={formData.discount}
              onChange={handleChange}
              className="product-form-input"
              placeholder="Ej: 10 para 10%"
              disabled={!!formData.discountInPesos} // desactivar si hay descuento en pesos
            />
          </div>

          <div className="product-form-group">
            <label className="product-form-label">Descuento en pesos ($):</label>
            <input
              type="number"
              name="discountInPesos"
              value={formData.discountInPesos}
              onChange={handleChange}
              className="product-form-input"
              placeholder="Ej: 100 para $100"
              disabled={!!formData.discount} // desactivar si hay descuento en porcentaje
            />
          </div>

            <div className="product-form-group">
              <label className="product-form-label">Imagen del Producto:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="product-form-input"
              />
              {preview && (
                <img src={preview} alt="Vista previa" className="product-form-preview" />
              )}
            </div>
            <button type="submit" className="product-form-button" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </form>
        </div>
      ) : (
        <p className="centered-text">Producto no encontrado.</p>
      )}
    </div>
  );
}

export default EditProduct;
